"use client"

import BreadCrumb from "@/components/breadcrumb"
import NormalContent from "@/components/normal-content"
import RelatedPostDetail from "@/components/related-post-detail"
import SocialList from "@/components/social-list"
import TableContent from "@/components/table-content"
import { t } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import { useNotificationMessages } from "@/lib/useNotificationMessages"
import { generateImagePath } from "@/lib/utils"
import { extractTableOfContents } from "@/utils/extract-table-of-contents"
import Image from "next/image"
import { useEffect, useMemo, useState } from "react"
import { PostData } from "../lib/config/types"
import { useCurrentPostStore } from "../store/currentPost"

type Props = {
  postData?: PostData
  hasBreadcrumb?: boolean
}

const PagePostDetail = ({ postData, hasBreadcrumb }: Props) => {
  const { contextHolderNotification } = useNotificationMessages()
  const { setPostId } = useCurrentPostStore()

  const lang = useMemo(() => {
    if (typeof document !== "undefined") return document.documentElement.lang || "en"
    return "en"
  }, [])

  const [share, setShare] = useState<{ url: string; title: string }>({ url: "", title: "" })

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return ""
    return new Intl.DateTimeFormat("vi-VN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(dateStr))
  }

  const breadcrumbItems = [
    {
      title: "Blog",
      link: lang === "en" ? "/blogs" : "/blogs-vi",
    },
    {
      title: postData?.attributes.name,
      link: "",
    },
  ]

  const content = postData?.attributes?.content || ""
  const { toc: tableOfContents, htmlWithIds } = useMemo(() => extractTableOfContents(content), [content])

  useEffect(() => {
    setShare({
      url: encodeURIComponent(window.location.href),
      title: encodeURIComponent(document.title || ""),
    })
    setPostId(postData?.id ? postData.id.toString() : null)
  }, [postData?.id])

  return (
    <div>
      {contextHolderNotification}
      {hasBreadcrumb && <BreadCrumb items={breadcrumbItems} />}

      <div className="max-w-[1200px] mx-auto flex flex-col">
        <div className="pt-4 sm:pt-8 relative">
          <div className="flex flex-col gap-16 relative sm:pb-[2rem] pb-10">
            <div className="flex flex-col gap-6 items-start max-w-[1000px] relative lg:px-0 px-4">
              {postData?.attributes?.tags?.data &&
                postData?.attributes?.tags?.data?.map((tag, index) => (
                  <p
                    key={index}
                    className="px-2 py-1.5 rounded-lg bg-[#0B9444] uppercase text-white font-semibold text-sm leading-4 inline-block text-center"
                  >
                    {tag.attributes.name}
                  </p>
                ))}
              <h1 className="text-[#242F3E] font-bold text-5xl leading-[56px]">{postData?.attributes.name}</h1>
              <div
                dangerouslySetInnerHTML={{ __html: postData?.attributes?.excerpt || "" }}
                className="text-xl font-normal text-[#4F4F4F]"
              />
              <div className="flex flex-row gap-16 items-center">
                <div className="flex flex-col gap-1 items-start">
                  <p className="uppercase text-[#999999] font-normal text-sm leading-5">{t("Written by")}</p>
                  <p className="text-[#242F3E] font-normal text-base leading-5 ">{t("Manager")}</p>
                </div>
                <div className="flex flex-col gap-1 items-start">
                  <p className="uppercase text-[#999999] font-normal text-sm leading-5 ">{t("Publish on")}</p>
                  <p className="text-[#242F3E] font-normal text-base leading-5 ">
                    {formatDate(postData?.attributes.updatedAt)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start gap-16 relative">
              <div className="hidden sm:flex flex-col gap-6 sticky top-24 lg:px-0 px-4">
                {tableOfContents.length > 0 && (
                  <div className="flex flex-col justify-center gap-4 ">
                    <h2 className="sm:text-sm sm:leading-5 font-normal uppercase text-[#999999]">
                      {t("Table of contents")}
                    </h2>
                    <TableContent items={tableOfContents} />
                  </div>
                )}
                <SocialList currentUrl={share.url} currentTitle={share.title} />
              </div>

              <div className="flex flex-col sm:max-w-[888px] ">
                <div className="flex flex-col sm:gap-16 gap-10 justify-center border-b border-[#EBEDF0] pb-16">
                  <div className="relative w-full sm:h-[499px] h-[228px] sm:rounded-2xl overflow-hidden">
                    <Image
                      src={
                        postData?.attributes?.image?.data?.attributes?.url
                          ? generateImagePath(postData?.attributes?.image?.data?.attributes?.url, "original")
                          : DefaultImage
                      }
                      alt={postData?.attributes.name || ""}
                      fill
                      className="w-full sm:h-[499px] h-[228px] sm:rounded-2xl object-cover"
                    />
                  </div>
                  <NormalContent content={htmlWithIds} tableOfContents={tableOfContents} />
                </div>
                <div className="w-full sm:hidden flex flex-col gap-4 items-start px-4 pt-10">
                  <div className="flex flex-col gap-1 items-start">
                    <p className="uppercase text-[#999999] font-normal text-sm leading-5">{t("Written by")}</p>
                    <p className="text-[#242F3E] font-normal text-base leading-5 ">{t("Manager")}</p>
                  </div>
                  <div className="flex flex-col gap-4 items-start">
                    <p className="uppercase text-[#999999] font-normal text-sm leading-5">{t("Share this post")}</p>
                    <SocialList currentUrl={share.url} currentTitle={share.title} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <section className="bg-[#F9F9FB]">
        <RelatedPostDetail pageSize="8" currentPostId={postData?.id} />
      </section>
    </div>
  )
}

export default PagePostDetail
